import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Check } from "lucide-react";

/**
 * The four course offerings. `onSelect` is only passed on the member dashboard,
 * where picking a course saves it against the membership.
 */
export const YogaCourses = ({ onSelect, selected, cta = "Join Course", onJoin, joining }) => {
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    api.get("/yoga/pricing").then(({ data }) => setCourses(data.courses || [])).catch(() => {});
  }, []);

  return (
    <section className="border-t border-border" id="courses" data-testid="yoga-courses">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="max-w-2xl">
          <div className="overline mb-3">Course Offerings</div>
          <h2 className="font-display text-4xl lg:text-5xl mb-4">Choose the path that fits you</h2>
          <p className="text-muted-foreground">
            Your membership opens every course below, and enrolled members can book a 1-on-1 evening
            session with the guru who specialises in it.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {courses.map((c) => {
            const isSelected = selected === c.slug;
            return (
              <Card
                key={c.slug}
                className={`border-border overflow-hidden transition-all ${isSelected ? "ring-2 ring-primary" : "hover:-translate-y-1"}`}
                data-testid={`course-card-${c.slug}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={c.image} alt={c.title} loading="lazy" className="w-full h-full object-cover" />
                </div>
                <CardContent className="p-5">
                  <div className="font-display text-xl leading-tight">{c.title}</div>
                  <p className="text-sm text-muted-foreground mt-2">{c.summary}</p>
                  <div className="text-xs text-secondary mt-3">{c.focus}</div>
                  {onSelect ? (
                    <Button
                      variant={isSelected ? "default" : "outline"}
                      size="sm"
                      onClick={() => onSelect(c.slug)}
                      className={`btn-pill mt-5 w-full ${isSelected ? "bg-primary text-primary-foreground" : ""}`}
                      data-testid={`course-select-${c.slug}`}
                    >
                      {isSelected ? <><Check size={13} className="mr-1.5"/> My course</> : "Choose this course"}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={onJoin}
                      disabled={joining}
                      className="btn-pill mt-5 w-full bg-primary text-primary-foreground"
                      data-testid={`course-join-${c.slug}`}
                    >
                      {joining ? "Opening…" : cta} <ArrowRight size={13} className="ml-1.5"/>
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
          <Badge variant="outline" className="border-secondary/40 text-secondary">1-on-1 with a guru</Badge>
          Daily 7:00 – 8:00 PM, by prior appointment from your member dashboard.
        </div>
      </div>
    </section>
  );
};

export default YogaCourses;
